import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChevronRight, ChevronLeft, MessageCircle, AlertCircle, CheckCircle2, Phone, Globe, Shield, Car, HeartPulse, Smile } from "lucide-react";
import { useConnectivity } from "@/hooks/use-connectivity";

// Links e Constantes
const WHATSAPP_LINK = "https://wa.me/5511947355276?text=Oi%2C%20vim%20pelo%20link%20da%20bio%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es.";
const PHONE_LINK = "tel:+5511947355276";

// Estrutura de Categorias e Subcategorias
const CATEGORIES = [
  { 
    id: 'seguros', 
    label: 'Seguros', 
    icon: Shield,
    subcategories: ['Seguro de Imóveis', 'Seguro de Veículos', 'Seguro de Vida', 'Outros Seguros'] 
  },
  { 
    id: 'consorcios', 
    label: 'Consórcios', 
    icon: Car,
    subcategories: ['Consórcio de Veículos', 'Consórcio de Imóveis', 'Consórcio de Motos', 'Outros Consórcios'] 
  },
  { 
    id: 'saude', 
    label: 'Planos de Saúde', 
    icon: HeartPulse,
    subcategories: ['Plano Individual', 'Plano Familiar', 'Plano Empresarial', 'Outros Planos de Saúde'] 
  },
  { 
    id: 'odonto', 
    label: 'Planos Odontológicos', 
    icon: Smile,
    subcategories: ['Odonto Individual', 'Odonto Familiar', 'Odonto Empresarial', 'Outros Planos Odonto'] 
  },
];

type ViewState = 'HOME' | 'SUBCATEGORIES' | 'FORM' | 'SUCCESS' | 'ERROR';

const Bio = () => {
  const isOffline = useConnectivity();
  const [view, setView] = useState<ViewState>('HOME');
  
  // States de seleção
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<string>("");

  // States do Form
  const [form, setForm] = useState({ name: "", email: "", phone_number: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Efeito de scroll to top ao trocar de view
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [view]);



  const activeCategory = CATEGORIES.find(c => c.id === selectedCategoryId);

  const handleCategoryClick = (categoryId: string) => {
    setSelectedCategoryId(categoryId);
    setView('SUBCATEGORIES');
  };

  const handleSubcategoryClick = (sub: string) => {
    setSelectedProduct(sub);
    setView('FORM');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isOffline) return;

    if (!form.name || !form.email || !selectedProduct) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch("https://form-handler.tatikka.workers.dev", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          formId: "tavares-camilo_contato",
          name: form.name,
          email: form.email,
          phone_number: form.phone_number,
          message: `[VIA LINK BIO] Produto de interesse: ${selectedProduct}`, 
        }),
      });

      if (response.ok) {
        setView('SUCCESS');
        setForm({ name: "", email: "", phone_number: "" });
      } else {
        setView('ERROR');
      }
    } catch (error) {
      console.error("Erro na requisição:", error);
      setView('ERROR');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-offwhite flex justify-center font-body relative overflow-x-hidden">
      {/* Forçar ocultação do ZapBalloon global via CSS nesta página */}
      <style>
        {`#zapballoon-widget { display: none !important; }`}
      </style>
      {/* ZapBalloon Global para a Bio */}
      <a 
        href={WHATSAPP_LINK}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 w-14 h-14 bg-[#25D366] text-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 hover:shadow-xl transition-all duration-300 z-50 animate-in fade-in zoom-in-50 delay-500 fill-mode-backwards"
        aria-label="Falar pelo WhatsApp"
      >
        <MessageCircle size={28} />
      </a>

      {/* Container simulador de App Mobile */}
      <div className="w-full max-w-md bg-white min-h-screen shadow-2xl relative flex flex-col">
        
        {/* VIEW: HOME */}
        {view === 'HOME' && (
          <div className="p-8 flex flex-col items-center animate-in fade-in slide-in-from-bottom-4 duration-300">
            
            {/* Logo */}
            <div className="mt-8 mb-12">
              <img 
                src="https://res.cloudinary.com/tatikka/image/upload/v1774976999/Logo_Simples_f4nl6q.svg" 
                alt="Tavares & Camilo" 
                className="h-12 w-auto"
              />
            </div>

            {/* Ações Rápidas Topo */}
            <div className="flex gap-4 w-full mb-8">
              <a href={PHONE_LINK} className="flex-1">
                <Button variant="secondary" className="w-full rounded-2xl h-12 font-bold text-navy bg-secondary/70 hover:bg-secondary border-none shadow-sm gap-2">
                  <Phone size={16} className="text-navy opacity-70" />
                  Ligar
                </Button>
              </a>
              <a href="/" className="flex-1">
                <Button variant="secondary" className="w-full rounded-2xl h-12 font-bold text-navy bg-secondary/70 hover:bg-secondary border-none shadow-sm gap-2">
                  <Globe size={16} className="text-navy opacity-70" />
                  Site
                </Button>
              </a>
            </div>

            {/* Ações Primárias */}
            <div className="w-full space-y-4 mb-12">
              <Button 
                onClick={() => { setSelectedProduct("Cotação Geral"); setView('FORM'); }}
                variant="hero" 
                className="w-full h-14 rounded-2xl text-base"
              >
                Solicitar uma Cotação
              </Button>
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="block w-full">
                <Button variant="outline" className="w-full h-14 rounded-2xl text-base font-semibold shadow-soft border-2 border-primary text-primary bg-white hover:bg-primary/5 gap-2">
                  <MessageCircle size={20} />
                  Falar com Especialista
                </Button>
              </a>
            </div>

            {/* Lista de Categorias */}
            <div className="w-full space-y-3">
              {CATEGORIES.map(cat => {
                const Icon = cat.icon;
                return (
                  <button 
                    key={cat.id}
                    onClick={() => handleCategoryClick(cat.id)}
                    className="w-full h-16 px-6 bg-secondary/40 hover:bg-secondary/80 text-navy font-semibold rounded-2xl flex items-center justify-between transition-colors border border-border/50 group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 group-hover:bg-primary group-hover:text-white transition-colors">
                        <Icon size={16} />
                      </div>
                      <span>{cat.label}</span>
                    </div>
                    <ChevronRight size={20} className="text-navy/40 group-hover:text-primary transition-colors shrink-0 ml-4" />
                  </button>
                );
              })}
            </div>
            
            <div className="mt-16 mb-8 text-center text-xs text-muted-foreground font-medium opacity-60">
              © {new Date().getFullYear()} Tavares & Camilo
            </div>
          </div>
        )}

        {/* VIEW: SUBCATEGORIES */}
        {view === 'SUBCATEGORIES' && activeCategory && (
          <div className="p-6 flex flex-col min-h-full animate-in fade-in slide-in-from-right-4 duration-300">
            <button 
              onClick={() => setView('HOME')}
              className="flex items-center text-sm font-semibold text-navy hover:text-primary transition-colors py-4 mb-4"
            >
              <ChevronLeft size={20} className="mr-1" /> Voltar
            </button>

            <div className="flex justify-center mb-10">
              <img src="https://res.cloudinary.com/tatikka/image/upload/v1774976999/Logo_Simples_f4nl6q.svg" alt="Tavares & Camilo" className="h-10 w-auto" />
            </div>

            <h2 className="font-heading font-bold text-2xl text-center text-navy mb-8">
              {activeCategory.label}
            </h2>

            <div className="space-y-3 w-full">
              {activeCategory.subcategories.map(sub => {
                const Icon = activeCategory.icon;
                return (
                  <button 
                    key={sub}
                    onClick={() => handleSubcategoryClick(sub)}
                    className="w-full h-16 px-6 bg-secondary/40 hover:bg-secondary/80 text-navy font-semibold rounded-2xl flex items-center justify-between transition-colors border border-border/50 group text-left"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        <Icon size={16} />
                      </div>
                      <span>{sub}</span>
                    </div>
                    <ChevronRight size={20} className="text-navy/40 group-hover:text-primary transition-colors shrink-0 ml-4" />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* VIEW: FORM */}
        {view === 'FORM' && (
          <div className="p-6 flex flex-col min-h-full animate-in fade-in slide-in-from-right-4 duration-300">
             <button 
              onClick={() => activeCategory ? setView('SUBCATEGORIES') : setView('HOME')}
              className="flex items-center text-sm font-semibold text-navy hover:text-primary transition-colors py-4 mb-4"
            >
              <ChevronLeft size={20} className="mr-1" /> Voltar
            </button>

            <div className="flex justify-center mb-10">
              <img src="https://res.cloudinary.com/tatikka/image/upload/v1774976999/Logo_Simples_f4nl6q.svg" alt="Tavares & Camilo" className="h-10 w-auto" />
            </div>

            <h2 className="font-heading font-bold text-2xl text-center text-navy mb-8">
              Solicitar uma Cotação
            </h2>

            <form onSubmit={handleSubmit} className="space-y-5 w-full">
              <div className="space-y-2">
                <Input
                  placeholder="Seu Nome"
                  value={form.name}
                  disabled={isOffline || isSubmitting}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="h-14 rounded-2xl bg-secondary/50 border-none font-semibold text-navy placeholder:text-navy/50 focus-visible:ring-primary/40 px-6"
                  required
                />
              </div>
              <div className="space-y-2">
                <Input
                  type="email"
                  placeholder="Seu E-mail"
                  value={form.email}
                  disabled={isOffline || isSubmitting}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="h-14 rounded-2xl bg-secondary/50 border-none font-semibold text-navy placeholder:text-navy/50 focus-visible:ring-primary/40 px-6"
                  required
                />
              </div>
              <div className="space-y-2">
                <Input
                  type="tel"
                  placeholder="Seu Telefone"
                  value={form.phone_number}
                  disabled={isOffline || isSubmitting}
                  onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
                  className="h-14 rounded-2xl bg-secondary/50 border-none font-semibold text-navy placeholder:text-navy/50 focus-visible:ring-primary/40 px-6"
                  required
                />
              </div>

              <div className="space-y-2 mt-2">
                <Label className="font-heading font-semibold text-navy block mb-3 px-2">
                  Qual produto precisa de cotação?
                </Label>
                <div className="relative">
                  <select 
                    value={selectedProduct}
                    onChange={(e) => setSelectedProduct(e.target.value)}
                    disabled={isOffline || isSubmitting}
                    className="w-full h-14 rounded-2xl bg-secondary/50 border-none font-semibold text-navy px-6 appearance-none focus:outline-none focus:ring-2 focus:ring-primary/40"
                    required
                  >
                    <option value="" disabled>Selecione aqui</option>
                    <optgroup label="Seguros">
                      {CATEGORIES.find(c => c.id === 'seguros')?.subcategories.map(s => <option key={s} value={s}>{s}</option>)}
                    </optgroup>
                    <optgroup label="Consórcios">
                      {CATEGORIES.find(c => c.id === 'consorcios')?.subcategories.map(s => <option key={s} value={s}>{s}</option>)}
                    </optgroup>
                    <optgroup label="Planos de Saúde">
                      {CATEGORIES.find(c => c.id === 'saude')?.subcategories.map(s => <option key={s} value={s}>{s}</option>)}
                    </optgroup>
                    <optgroup label="Planos Odontológicos">
                      {CATEGORIES.find(c => c.id === 'odonto')?.subcategories.map(s => <option key={s} value={s}>{s}</option>)}
                    </optgroup>
                    <option value="Outro / Cotação Geral">Outro / Cotação Geral</option>
                  </select>
                  <div className="absolute right-6 top-1/2 -translate-y-1/2 pointer-events-none">
                    <div className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[8px] border-t-navy"></div>
                  </div>
                </div>
              </div>

              <div className="pt-6">
                <Button 
                  type="submit" 
                  variant="hero" 
                  className="w-full h-14 rounded-2xl text-base shadow-soft"
                  disabled={isSubmitting || isOffline}
                >
                  {isSubmitting ? "Enviando..." : isOffline ? "Você está offline" : "Enviar"}
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* VIEW: SUCCESS */}
        {view === 'SUCCESS' && (
          <div className="p-8 flex flex-col items-center justify-center min-h-full flex-1 animate-in fade-in zoom-in-95 duration-500">
            <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center mb-8 text-primary animate-pulse">
               <CheckCircle2 size={48} strokeWidth={2.5} />
            </div>
            <h2 className="font-heading font-bold text-3xl text-center text-navy mb-4">
              Enviado com<br/>Sucesso!
            </h2>
            <p className="font-body text-muted-foreground text-center mb-12 px-4">
              Em breve nossa equipe entrará em contato
            </p>
            <a href="/" className="w-full">
              <Button variant="secondary" className="w-full h-14 rounded-2xl text-base font-bold bg-secondary/60 hover:bg-secondary border-none">
                Acessar site
              </Button>
            </a>
          </div>
        )}

        {/* VIEW: ERROR */}
        {view === 'ERROR' && (
          <div className="p-8 flex flex-col items-center justify-center min-h-full flex-1 animate-in fade-in zoom-in-95 duration-500">
            <div className="w-24 h-24 bg-destructive/10 rounded-full flex items-center justify-center mb-8 text-destructive">
               <AlertCircle size={48} strokeWidth={2.5} />
            </div>
            <h2 className="font-heading font-bold text-3xl text-center text-navy mb-4">
              Ops! Algo<br/>deu errado.
            </h2>
            <p className="font-body text-muted-foreground text-center mb-12 px-4">
              Não conseguimos processar o envio.
            </p>
            
            <div className="w-full space-y-4">
              <Button 
                onClick={() => setView('FORM')}
                variant="secondary" 
                className="w-full h-14 rounded-2xl text-base font-bold bg-secondary/60 hover:bg-secondary border-none"
              >
                Tentar novamente
              </Button>
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="block w-full">
                <Button variant="hero" className="w-full h-14 rounded-2xl text-base shadow-soft">
                  Falar agora pelo WhatsApp
                </Button>
              </a>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default Bio;
